<app-content-section>
  <!-- prettier-ignore -->
  <p>
    Follow the steps below to get ready for your first
    <a [routerLink]="'/'">live online bass lesson</a>.
  </p>
  <p class="print-guide">
    <a
      href="/bass-lessons/zoom-setup-guide.pdf"
      download="zoom-setup-guide.pdf"
    >
      Download the setup guide (PDF)
    </a>
    to print or keep on your phone. It is the short version of this page.
  </p>
</app-content-section>

<app-jump-nav [links]="jumpNavLinks" />

<!-- The model the whole page uses: five jobs, one rule. Replaces the old
     "How the Audio Works" boxes, which described one mixer instead of the
     jobs any setup has to do. -->
<hr class="section-divider" />
<app-content-section heading="How lesson audio works" sectionId="five-jobs">
  <p>
    Every setup on this page does the same five things. Once you can name them,
    you can look at any mixer or interface you already own and see which piece
    does which job.
  </p>
  <p>{{ callEnds }}</p>
  <figure class="flow-figure">
    <app-signal-flow-diagram />
    <figcaption>
      The recommended setup. Solid arrows are what you send; dashed arrows are
      Russ coming back; the crossed arrow is what the Record Source switch
      prevents.
    </figcaption>
  </figure>
  <ol class="five-jobs-list" aria-label="The five jobs">
    @for (job of lessonJobs; track job.id) {
      <li>
        <strong>{{ job.label }}</strong>
        <span>{{ job.summary }}</span>
      </li>
    }
  </ol>
  <app-callout heading="The one rule">
    <p>{{ lessonRule }}</p>
  </app-callout>
  <p>
    On the recommended mixer the rule is a physical switch, Record Source set to
    INPUT MIX. On an audio interface it is which virtual device you tell Zoom to
    send. Same rule, different knob.
  </p>
</app-content-section>

<!-- Shopping Checklist -->
<hr class="section-divider" />
<app-content-section heading="Shopping List" sectionId="shopping-list">
  <p>
    Start with the recommended setup: a small mixer and a headset. It is the
    least gear, the fewest cables, and what I recommend to new students. If you
    already own headphones, a microphone, a DI, or an interface, keep them; the
    options that improve the lesson show where each one plugs in.
  </p>

  <h3 class="platform-heading">Recommended setup ({{ shoppingTotal }})</h3>
  <p>A small mixer and a headset: the least gear and the fewest cables.</p>
  <table class="shopping-list">
    <thead>
      <tr>
        <th scope="col" class="groove-type-label">Item</th>
        <th scope="col" class="groove-type-label">Price</th>
        <th scope="col" class="groove-type-label">Notes</th>
      </tr>
    </thead>
    <tbody>
      @for (item of shopping; track item.name) {
        <tr>
          <td>
            @if (item.href !== undefined) {
              <a openNewTab [href]="item.href">{{ item.name }}</a>
            } @else {
              {{ item.name }}
            }
          </td>
          <td>{{ item.price }}</td>
          <td>{{ item.notes }}</td>
        </tr>
      }
    </tbody>
  </table>

  <p>{{ whatIUse }}</p>

  <h3 class="platform-heading">You May Also Need</h3>
  <ul>
    <li>
      <a
        openNewTab
        href="https://www.amazon.com/s?k=USB-C+to+3.5mm+headphone+adapter"
      >
        <strong>USB-C to 3.5mm adapter</strong>
      </a>
      (~$8) &mdash; if your phone doesn't have a headphone jack.
    </li>
    <li>
      <a
        openNewTab
        href="https://www.amazon.com/s?k=3.5mm+to+1%2F4+inch+headphone+adapter+plug"
      >
        <strong>1/4-inch to 3.5mm adapter</strong>
      </a>
      (~$8) &mdash; only if your headphones have a 3.5mm plug (the small plug)
      instead of 1/4-inch (the large plug).
    </li>
  </ul>

  <p>
    Already own a mixer or an audio interface? See the
    <a routerLink="." fragment="other-setups">other setups</a>. Want a better
    mic, a DI, or louder headphones? See the
    <a routerLink="." fragment="improve">options that improve the lesson</a>.
  </p>
</app-content-section>

<!-- Cable & Gear Reference -->
<hr class="section-divider" />
<app-content-section
  heading="What Your Cables and Gear Look Like"
  sectionId="gear-reference"
  style="--_measure: none"
>
  <p>
    Not sure what these cables are? Here's a visual reference so you know
    exactly what to buy.
  </p>

  <div class="gear-gallery">
    <app-lightbox-image
      src="/images/zoom-setup/rockville-rockmix4-russ.webp"
      alt="Russ's Rockville RockMix 4 USB mixer, top view — the RECORD SOURCE (TO PC) switch is visible on the right"
      caption="Russ's RockMix 4 — note the RECORD SOURCE switch"
      aspectRatio="900 / 1252"
      sizes="(max-width: 600px) 45vw, 30vw"
    />
    <app-lightbox-image
      src="/images/zoom-setup/shure-sm58.webp"
      alt="Shure SM58 dynamic microphone"
      caption="Shure SM58 microphone"
      aspectRatio="750 / 750"
      sizes="(max-width: 600px) 45vw, 30vw"
    />
    <app-lightbox-image
      src="/images/zoom-setup/xlr-cable.webp"
      alt="XLR microphone cable with round connectors on each end"
      caption="XLR cable"
      aspectRatio="1225 / 1500"
      sizes="(max-width: 600px) 45vw, 30vw"
    />
    <app-lightbox-image
      src="/images/zoom-setup/xlr-male-female.webp"
      alt="XLR cable connectors: the male end (pins) plugs into the mixer, the female end (holes) plugs into the microphone"
      caption="XLR ends — male (pins) to mixer, female (holes) to microphone"
      aspectRatio="1357 / 1331"
      sizes="(max-width: 600px) 45vw, 30vw"
    />
    <app-lightbox-image
      src="/images/zoom-setup/instrument-cable.webp"
      alt="1/4-inch instrument cable with large mono plugs on each end"
      caption="1/4-inch instrument cable"
      aspectRatio="1500 / 1500"
      sizes="(max-width: 600px) 45vw, 30vw"
    />
    <app-lightbox-image
      src="/images/zoom-setup/35mm-to-dual-quarter-inch-cable.webp"
      alt="3.5mm to dual 1/4-inch stereo breakout cable: one small plug splits into two large plugs"
      caption="3.5mm to dual 1/4-inch cable (playback device to mixer)"
      aspectRatio="1306 / 1405"
      sizes="(max-width: 600px) 45vw, 30vw"
    />
    <app-lightbox-image
      src="/images/zoom-setup/oneodio-pro10-headphones.webp"
      alt="OneOdio Pro-10 over-ear headphones with detachable 1/4-inch cable"
      caption="OneOdio Pro-10 headphones"
      aspectRatio="1413 / 1500"
      sizes="(max-width: 600px) 45vw, 30vw"
    />
    <app-lightbox-image
      src="/images/zoom-setup/usbc-to-35mm-adapter.webp"
      alt="USB-C to 3.5mm headphone adapter dongle"
      caption="USB-C to 3.5mm adapter"
      aspectRatio="1369 / 1500"
      sizes="(max-width: 600px) 45vw, 30vw"
    />
    <app-lightbox-image
      src="/images/zoom-setup/quarter-inch-to-35mm-adapter.webp"
      alt="1/4-inch to 3.5mm headphone adapter plug"
      caption="1/4-inch to 3.5mm adapter"
      aspectRatio="1500 / 1360"
      sizes="(max-width: 600px) 45vw, 30vw"
    />
  </div>
</app-content-section>

<!-- Setup Steps -->
<hr class="section-divider" />
<app-content-section heading="Setup Steps" sectionId="setup-steps">
  <p>
    Follow these steps to connect all your gear. Use the diagrams below to find
    each connection on the mixer.
  </p>

  <!-- Annotated mixer diagrams — shown before steps for reference -->
  <h3 class="platform-heading">Mixer — Top View</h3>
  <figure class="annotated-mixer">
    <div class="variable-size-image" style="aspect-ratio: 900 / 1252">
      <img
        ngSrc="/images/zoom-setup/rockville-rockmix4-russ.webp"
        fill
        sizes="100vw"
        alt="Top view of Russ's Rockville RockMix 4 mixer with labeled connections: CH1 for microphone, CH2 for bass, AUX/MP3 IN for playback audio, PHONES for headphones, and the Record Source switch set to INPUT MIX"
      />
      <a
        routerLink="."
        fragment="step-mic"
        class="mixer-label"
        style="top: 6%; inset-inline-start: 16%"
      >
        Step 1: Mic
      </a>
      <a
        routerLink="."
        fragment="step-bass"
        class="mixer-label mixer-label--arrow-up"
        style="top: 18%; inset-inline-start: 36%"
      >
        Step 2: Bass
      </a>
      <a
        routerLink="."
        fragment="step-playback"
        class="mixer-label"
        style="top: 13%; inset-inline-start: 55%"
      >
        Step 3: Playback
        <span class="mixer-label-up-arrow" aria-hidden="true"></span>
      </a>
      <a
        routerLink="."
        fragment="step-headphones"
        class="mixer-label"
        style="top: 22%; inset-inline-start: 83%"
      >
        Step 4: Headphones
      </a>
      <a
        routerLink="."
        fragment="step-record-source"
        class="mixer-label"
        style="top: 58%; inset-inline-start: 62%"
      >
        Step 5: Record Source
      </a>
    </div>
    <figcaption>
      Top view &mdash; click any label to jump to that setup step
    </figcaption>
  </figure>

  <h3 class="platform-heading">Mixer — Back Panel</h3>
  <figure class="annotated-mixer">
    <div class="variable-size-image" style="aspect-ratio: 1600 / 645">
      <img
        ngSrc="/images/zoom-setup/art-usbmix4-back-io.webp"
        fill
        sizes="100vw"
        alt="Back panel of the ART USB Mix 4 mixer showing USB TO PC port and 5V DC power input"
      />
      <a
        routerLink="."
        fragment="step-mixer"
        class="mixer-label"
        style="top: 20%; inset-inline-start: 28%"
      >
        Step 6: USB to computer
      </a>
      <a
        routerLink="."
        fragment="step-mixer"
        class="mixer-label mixer-label--arrow-up"
        style="top: 64%; inset-inline-start: 42%"
      >
        Step 6: Power cable
      </a>
    </div>
    <figcaption>Back panel &mdash; USB and power connections</figcaption>
  </figure>

  <ol class="setup-steps">
    <!-- Step 1 -->
    <li id="step-mic">
      <h3 class="step-heading">{{ stepTitle("step-mic") }}</h3>
      <p class="step-job groove-type-label">{{ stepJobs("step-mic") }}</p>
      <div class="step-body">
        <p>
          Place your microphone in a desk stand and position it near your mouth.
          Plug one end of the XLR cable into your microphone and the other end
          into the CH1 input on the mixer.
        </p>
        <p>
          <strong>About phantom power:</strong>
          The Shure SM58 does not need phantom power. If you use a condenser
          microphone that requires it, turn on the +48V switch on the mixer.
          Otherwise, leave it off.
        </p>
      </div>
      <app-lightbox-image
        src="/images/zoom-setup/xlr-male-female.webp"
        alt="XLR cable connectors: the male end (pins) plugs into the mixer CH1 input, the female end (holes) plugs into the microphone"
        caption="XLR cable — male end (pins) to mixer, female end (holes) to microphone"
        aspectRatio="1357 / 1331"
      />
    </li>

    <!-- Step 2 -->
    <li id="step-bass">
      <h3 class="step-heading">{{ stepTitle("step-bass") }}</h3>
      <p class="step-job groove-type-label">{{ stepJobs("step-bass") }}</p>
      <div class="step-body">
        <p>
          Plug your 1/4-inch instrument cable (the larger plug) from your bass
          into the CH2 input on the mixer.
        </p>
        <p>
          <strong>About Hi-Z:</strong>
          If your bass is passive (no battery compartment), press the INST/Hi-Z
          button on the back of the mixer. If your bass has an active preamp
          (battery compartment), leave this button off.
        </p>
      </div>
      <app-lightbox-image
        src="/images/zoom-setup/instrument-cable.webp"
        alt="1/4-inch instrument cable with large mono plug on each end"
        caption="1/4-inch instrument cable — plug one end into your bass, the other into CH2"
        aspectRatio="1500 / 1500"
      />
    </li>

    <!-- Step 3 -->
    <li id="step-playback">
      <h3 class="step-heading">{{ stepTitle("step-playback") }}</h3>
      <p class="step-job groove-type-label">
        {{ stepJobs("step-playback") }}
      </p>
      <div class="step-body">
        <p>
          Your playback device can be a phone, tablet, or computer &mdash;
          anything that plays backing tracks, metronome apps, or lesson
          materials. See
          <a routerLink="." fragment="apps-for-lessons"> apps for lessons </a>
          for the specific tools I recommend students use.
        </p>
        <p>
          Plug the 3.5mm end (the small plug) of the cable into your device's
          headphone jack.
        </p>
        <p>
          Plug the two 1/4-inch ends (the larger plugs) into the CH3 (L/left)
          and CH4 (R/right) inputs on the mixer.
        </p>
        <p>
          <strong>No headphone jack?</strong>
          Use a USB-C to 3.5mm adapter (or Lightning to 3.5mm for older
          iPhones).
        </p>
        <app-callout heading="Tip">
          <p>
            You can also use the AUX/MP3 IN jack (3.5mm on the top of the mixer)
            with a simple 3.5mm-to-3.5mm cable. This is easier to connect, but
            you won't have a volume knob on the mixer for playback audio.
          </p>
        </app-callout>
        <p>
          Set your playback device's volume to maximum. Use the CH3/4 knob on
          the mixer to control the playback volume.
        </p>
      </div>
      <app-lightbox-image
        src="/images/zoom-setup/35mm-to-dual-quarter-inch-cable.webp"
        alt="3.5mm to dual 1/4-inch stereo breakout cable: one small plug splits into two large plugs"
        caption="3.5mm to dual 1/4-inch cable — small plug to your device, two large plugs to CH3/CH4"
        aspectRatio="1306 / 1405"
      />
    </li>

    <!-- Step 4 -->
    <li id="step-headphones">
      <h3 class="step-heading">{{ stepTitle("step-headphones") }}</h3>
      <p class="step-job groove-type-label">
        {{ stepJobs("step-headphones") }}
      </p>
      <div class="step-body">
        <p>
          If you're using the recommended headset, plug the mic cable from its
          Y-splitter into the HEADSET mic jack (3.5mm) on the mixer, and the
          headphone cable from the Y-splitter into the HEADSET headphone jack
          (3.5mm).
        </p>
        <p>
          If you're using separate headphones and a microphone instead, plug
          your headphones into the PHONES output (1/4-inch jack on the front of
          the mixer). Use a 1/4-inch to 3.5mm adapter if your headphones have a
          3.5mm plug.
        </p>
        <app-callout heading="Warning" variant="warning">
          <p>
            Do not use the HEADSET jack on the mixer if you are using a separate
            microphone in CH1. Plugging into the HEADSET jack disables the CH1
            input.
          </p>
        </app-callout>
      </div>
      <app-lightbox-image
        src="/images/zoom-setup/oneodio-pro10-headphones.webp"
        alt="OneOdio Pro-10 over-ear headphones with detachable 1/4-inch and 3.5mm cables"
        caption="OneOdio Pro-10 headphones — includes both 1/4-inch and 3.5mm cables"
        aspectRatio="1413 / 1500"
      />
    </li>

    <!-- Step 5 -->
    <li id="step-record-source">
      <h3 class="step-heading">{{ stepTitle("step-record-source") }}</h3>
      <p class="step-job groove-type-label">
        {{ stepJobs("step-record-source") }}
      </p>
      <div class="step-body">
        <p>
          Find the RECORD SOURCE (TO PC) switch on the top of the mixer. Set it
          to
          <strong>INPUT MIX</strong>.
        </p>
        <app-callout heading="This is the most important setting on the mixer">
          <p>
            INPUT MIX sends your voice, bass, and backing tracks to Zoom &mdash;
            but does not send Zoom's audio back. This prevents echo and
            feedback.
          </p>
        </app-callout>
        <app-callout heading="Warning" variant="warning">
          <p>
            Do not use the LOOPBACK setting. It will send Zoom's audio back to
            Zoom, creating an unusable echo for me.
          </p>
        </app-callout>
        <p>See the top view diagram above to find the Record Source switch.</p>
        <p>
          This step is the no-loopback job. It is the one setting on the whole
          board that protects both of us from echo.
        </p>
      </div>
    </li>

    <!-- Step 6 -->
    <li id="step-mixer">
      <h3 class="step-heading">{{ stepTitle("step-mixer") }}</h3>
      <p class="step-job groove-type-label">{{ stepJobs("step-mixer") }}</p>
      <div class="step-body">
        <p>
          Plug the included USB cable from the back of the mixer into your
          computer. This is how the mixer sends and receives audio with Zoom.
        </p>
        <p>
          Connect the included power cable (micro USB) to power the mixer. Plug
          the other end into a wall outlet or power strip.
        </p>
        <p>
          Your computer recognizes the mixer automatically &mdash; no software
          or drivers to install. See the back panel diagram above.
        </p>
      </div>
    </li>
  </ol>

  <app-five-jobs-table
    caption="The five jobs in the recommended setup"
    [rows]="recommended.rows"
  />

  <h3 class="platform-heading">If it goes wrong</h3>
  <p>The first five things I check with a new student, in this order.</p>
  @for (item of recommended.troubleshooting; track item.symptom) {
    <app-disclosure [summary]="item.symptom">
      <p><strong>Check:</strong> {{ item.check }}</p>
      <p><strong>Fix:</strong> {{ item.fix }}</p>
    </app-disclosure>
  }
</app-content-section>

<!-- Computer Audio Settings -->
<hr class="section-divider" />
<app-content-section
  heading="Computer Audio Settings"
  sectionId="computer-settings"
>
  <p>
    Tell your computer to send audio through the USB mixer so you hear
    everything in your headphones.
  </p>

  <h3 class="platform-heading">Mac</h3>
  <p>
    Open System Settings &gt; Sound &gt; Output. Select the USB mixer (it
    appears as "USB Audio CODEC" or something similar).
  </p>
  <!-- TODO: Screenshot placeholder: macOS System Settings Sound Output panel with USB Audio CODEC selected -->

  <h3 class="platform-heading">Windows</h3>
  <p>
    Open Settings &gt; System &gt; Sound &gt; Output device. Select the USB
    mixer.
  </p>
  <p>
    Windows 10 has a similar path &mdash; look for Settings &gt; System &gt;
    Sound.
  </p>
  <!-- TODO: Screenshot placeholder: Windows 11 Sound Settings showing USB mixer selected as output device -->

  <app-callout heading="Important" variant="warning">
    <p>
      If you are using your computer as the playback device (instead of a phone
      or tablet), do
      <strong>not</strong>
      set your system audio output to the USB mixer. Set it to your computer's
      built-in speakers or headphone jack instead. Only Zoom should send audio
      to the mixer. If both Zoom and your system audio go through the mixer,
      Zoom's audio will loop back and create echo.
    </p>
  </app-callout>
</app-content-section>

<!-- Zoom Audio Settings -->
<hr class="section-divider" />
<app-content-section heading="Zoom Audio Settings" sectionId="zoom-settings">
  <p>
    In
    <a openNewTab href="https://zoom.us/download">Zoom</a>
    , go to Settings &gt; Audio.
  </p>
  <ul>
    <li>
      <strong>Microphone:</strong>
      select the USB mixer.
    </li>
    <li>
      <strong>Speaker:</strong>
      select the USB mixer.
    </li>
    <li>
      Enable
      <strong>Original Sound for Musicians</strong>
      &mdash; this preserves your bass tone. Zoom's default audio processing
      filters out low frequencies, which makes your bass sound thin and distant.
    </li>
  </ul>
  <!-- TODO: Screenshot placeholder: Zoom Audio Settings panel showing USB mixer selected for Speaker and Microphone, with Original Sound for Musicians enabled -->

  <app-callout heading="If your computer is the playback device">
    <p>
      In Zoom, set Speaker to the USB mixer. But your system audio output
      (outside Zoom) should go to your computer's built-in speakers or headphone
      jack &mdash; not the mixer.
    </p>
  </app-callout>
</app-content-section>

<!-- Mixer Settings & Levels -->
<hr class="section-divider" />
<app-content-section
  heading="Mixer Settings &amp; Levels"
  sectionId="mixer-levels"
>
  <p>
    These are good starting points. You can fine-tune during your first lesson.
  </p>

  <dl class="level-settings">
    <dt>Record Source</dt>
    <dd>
      <strong>INPUT MIX</strong>
      (see
      <a routerLink="." fragment="step-record-source">Step 5</a>
      above).
    </dd>

    <dt>CH1 (voice)</dt>
    <dd>
      Start at 5. Adjust so the green LED lights on peaks &mdash; if the red LED
      stays on, turn it down.
    </dd>

    <dt>CH2 (bass)</dt>
    <dd>
      Start at 5. Same as above &mdash; green LED on peaks, no steady red.
    </dd>

    <dt>Gain (CH1 and CH2)</dt>
    <dd>
      Start at the midpoint (12 o'clock). Turn down if the red clip LED stays
      lit.
    </dd>

    <dt>CH3/4 (playback)</dt>
    <dd>
      Start at 5. Adjust for comfortable backing track volume in your
      headphones.
    </dd>

    <dt>COMPUTER (teacher's audio from Zoom)</dt>
    <dd>Start at 5. Adjust so my voice is comfortable in your headphones.</dd>

    <dt>PHONES/MONITOR</dt>
    <dd>Adjust for comfortable headphone volume.</dd>

    <dt>MAIN MIX</dt>
    <dd>Start at 5. Controls the overall level sent to Zoom.</dd>

    <dt>EQ (HIGH and LOW)</dt>
    <dd>
      Leave at center (12 o'clock) &mdash; flat, no boost or cut. Adjust later
      if needed.
    </dd>

    <dt>PAN (CH1 and CH2)</dt>
    <dd>Leave at center.</dd>

    <dt>Phantom power (+48V)</dt>
    <dd>
      Leave OFF unless your microphone requires it. The Shure SM58 does not need
      phantom power.
    </dd>
  </dl>
</app-content-section>

<!-- Options that improve the lesson: substitutions for the swappable pieces,
     organized by the job they do. Whole-setup alternatives are the next
     section, because their jobs converge and cannot be listed piecemeal. -->
<hr class="section-divider" />
<app-content-section [heading]="improvementsHeading" sectionId="improve">
  <p>{{ improvementsIntro }}</p>
  @for (item of adaptations; track item.id) {
    <app-disclosure [summary]="item.title">
      <p class="step-job groove-type-label">Job: {{ jobLabel(item.job) }}</p>
      <p>{{ item.what }}</p>
      <p><strong>You will also need:</strong> {{ item.extra }}</p>
      <h3 class="platform-heading">If it goes wrong</h3>
      @for (t of item.troubleshooting; track t.symptom) {
        <p>
          <strong>{{ t.symptom }}</strong> Check: {{ t.check }} Fix: {{ t.fix }}
        </p>
      }
    </app-disclosure>
  }
</app-content-section>

<!-- Other whole setups: worked examples with their own five-jobs table. -->
<hr class="section-divider" />
<app-content-section heading="Other whole setups" sectionId="other-setups">
  <p>
    Two setups I see often enough to walk through. Each one does the same five
    jobs; the table shows which piece does which.
  </p>
  @for (profile of otherSetups; track profile.id) {
    <h3 class="platform-heading">{{ profile.name }}</h3>
    @if (profile.id === "mixer-no-switch") {
      <p>
        Most cheap USB mixers send everything they receive back over USB,
        including my audio from Zoom. Nothing on the board does the no-loopback
        job, so you do it by staying muted in Zoom when you are not playing or
        talking. It works. It is not ideal, because I lose the ability to hear
        you all the time.
      </p>
    }
    @if (profile.id === "interface-virtual-devices") {
      <p>
        If you already own an audio interface, this is how it does the five
        jobs. Bass and mic go into the interface. A virtual-audio app (Loopback
        on the Mac, VoiceMeeter is the Windows equivalent) makes two virtual
        devices: one that collects your playback app, and one that holds bass,
        mic and playback for Zoom to send. Zoom's own output goes to the
        playback device, so it is never in the device Zoom sends. Everything is
        mixed in software; the jobs do not go away, they just share a box. I
        have not written a click-by-click walkthrough for this one; the table is
        the map, and I can talk you through the rest in a lesson.
      </p>
    }
    <app-five-jobs-table
      [caption]="captionFor(profile)"
      [rows]="profile.rows"
    />
    <h4 class="platform-heading">If it goes wrong</h4>
    @for (item of profile.troubleshooting; track item.symptom) {
      <app-disclosure [summary]="item.symptom">
        <p><strong>Check:</strong> {{ item.check }}</p>
        <p><strong>Fix:</strong> {{ item.fix }}</p>
      </app-disclosure>
    }
  }
</app-content-section>

<!-- General troubleshooting: not about any one setup. Kept short on
     purpose; the setup-specific lists live with their setups above. -->
<hr class="section-divider" />
<app-content-section heading="Troubleshooting" sectionId="troubleshooting">
  <p>
    Before you blame the gear, check these three. The second one is the one
    everybody skips.
  </p>
  @for (item of generalTroubleshooting; track item.symptom) {
    <app-disclosure [summary]="item.symptom">
      <p><strong>Check:</strong> {{ item.check }}</p>
      <p><strong>Fix:</strong> {{ item.fix }}</p>
    </app-disclosure>
  }
</app-content-section>

<!-- Tips -->
<hr class="section-divider" />
<app-content-section
  heading="Tips for a Better Lesson Experience"
  sectionId="tips"
>
  <ul>
    <li>
      <strong>Camera positioning:</strong>
      Mount an external webcam on a stand, angled down to show your fretboard
      and hands. This helps me see your technique clearly.
    </li>
    <li>
      <strong>External monitor:</strong>
      Use a second screen for lesson materials, PDFs, or sheet music while
      keeping Zoom on your main screen.
    </li>
  </ul>
</app-content-section>

<!-- Apps for Lessons -->
<hr class="section-divider" />
<app-content-section heading="Apps for lessons" sectionId="apps-for-lessons">
  <p>
    Apps I recommend for bass students to practice more effectively between
    lessons.
  </p>
  @for (app of practiceApps; track app.name) {
    <app-resource-row
      [name]="app.name"
      [href]="app.href"
      [description]="app.description"
    />
  }
</app-content-section>

<!-- Print Guide + Contact -->
<hr class="section-divider" />
<section class="print-guide">
  <p>
    <a
      href="/bass-lessons/zoom-setup-guide.pdf"
      download="zoom-setup-guide.pdf"
    >
      Download the setup guide (PDF)
    </a>
    to print or keep on your phone. It is the short version of this page.
  </p>
</section>
